/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import USLayout from "./keyboard_layouts/USLayout";
import NumLayout from "./keyboard_layouts/NumLayout";
import KeyboardButton from "./KeyboardButton";
import BackgroundPanel from "../BackgroundPanel";
import EventHandlersManager from "../../../controllers/EventHandlersManager";

const keyboardType = (type) => {
    switch (type) {
        case "us":
            return USLayout;
        case "num":
            return NumLayout;
        default:
            console.error("Invalid keyboard type");
    }
};

function Keyboard({
    fontColor = "#c6c6c6",
    highlightColor = "#363636",
    backgroundColor = "#121212",
    fontSize = 0.39,
    buttonColor = "#262626",
    isVR,
    state,
    setKeyboard,
    aframeClasses,
    ...otherAttributes
}) {
    const root = useRef();
    useEffect(() => {
        if (state && !state.visible) return;
        const cameraPosition = document
            .querySelector("a-camera")
            .getAttribute("position");
        const cameraRotation = document
            .querySelector("a-camera")
            .getAttribute("rotation");
        root.current.object3D.position.set(
            cameraPosition.x,
            cameraPosition.y,
            cameraPosition.z
        );
        root.current.setAttribute("rotation", {
            X: 0,
            y: cameraRotation.y,
            z: 0
        });
    }, [root, state]);
    const [mode, setMode] = useState("normal");

    if (!state.visible) {
        if (mode !== "normal") setMode("normal");
        return null;
    }

    const KEYBOARD_PADDING = 0.01;
    const KEY_PADDING = 0.004;
    const KEY_SIZE = 0.04;

    const keyboardKeys = keyboardType(state.type);
    if (!keyboardKeys) return null;

    const KEYS_FOR_ROW = keyboardKeys[mode][0].length;

    const KEYBOARD_WIDTH =
        KEY_SIZE * KEYS_FOR_ROW +
        KEY_PADDING * (KEYS_FOR_ROW - 1) +
        KEYBOARD_PADDING * 2;
    const KEYBOARD_HEIGHT =
        KEY_SIZE * keyboardKeys[mode].length +
        KEY_PADDING * (keyboardKeys[mode].length - 1) +
        KEYBOARD_PADDING * 2;
    const clickHandler = (key) => {
        if (!key) return;
        switch (key) {
            case "<-":
            case "Backspace":
                state.callback((oldInput) => {
                    return oldInput.slice(0, -1);
                });
                break;
            case "Shift":
                if (isVR && keyboardKeys.shift)
                    mode !== "shift" ? setMode("shift") : setMode("normal");
                break;
            case "Alt":
                if (isVR && keyboardKeys.alt)
                    mode !== "alt" ? setMode("alt") : setMode("normal");
                break;
            case "Close":
            case "Escape":
                state.callback(() => "");
            case "Ent":
            case "Enter":
            case "Save":
                setKeyboard(null, false, null);
                setMode("normal");
                break;
            default:
                if (
                    (state.type === "num" && !"0123456789.".includes(key)) ||
                    !`abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890àèìòùÀÈÌÒÙ .,/<>:;'"[]{}=+-_()*&^%$#@!\|?`.includes(
                        key
                    )
                )
                    return;
                state.callback((oldInput) => {
                    return oldInput + key;
                });
                break;
        }
    };

    const drawKey = (keyObject) => {
        const type = keyObject.type;
        const value = keyObject.value;
        switch (type) {
            case "2vertical":
                return {
                    size: `${KEY_SIZE} ${KEY_SIZE * 2 + KEY_PADDING} 0`,
                    value
                };
            case "5horizontal":
                return {
                    size: `${KEY_SIZE * 5 + KEY_PADDING * 4} ${KEY_SIZE} 0`,
                    value
                };
            case "2horizontal":
                return {
                    size: `${KEY_SIZE * 2 + KEY_PADDING} ${KEY_SIZE} 0`,
                    value
                };
            default:
                return {
                    size: `${KEY_SIZE} ${KEY_SIZE} 0`,
                    value
                };
        }
    };

    const drawButton = () => {
        let positionY = 0;
        return keyboardKeys[mode].map((elem, index) => {
            let positionX = 0;
            return elem.map((e, i) => {
                const key = drawKey(e);
                const width = key.size.split(" ")[0];
                const height = key.size.split(" ")[1];
                const kb = (
                    <KeyboardButton
                        key={e.value + mode}
                        buttonKey={key}
                        width={width}
                        height={height}
                        fontSize={fontSize}
                        buttonColor={buttonColor}
                        highlightColor={highlightColor}
                        fontColor={fontColor}
                        clickHandler={clickHandler}
                        aframeClasses={aframeClasses}
                        position={`${
                            positionX +
                            width / 2 -
                            KEYBOARD_WIDTH / 2 +
                            KEYBOARD_PADDING
                        } ${
                            positionY -
                            height / 2 +
                            KEYBOARD_HEIGHT / 2 -
                            KEYBOARD_PADDING
                        } 0.001`}
                    />
                );
                positionX += parseFloat(width) + KEY_PADDING;
                if (elem.length === i + 1) {
                    positionY -= KEY_SIZE + KEY_PADDING;
                }
                return kb;
            });
        });
    };
    return (
        <a-entity class="Keyboard" ref={root} {...otherAttributes}>
            {!isVR && (
                <EventHandlersManager
                    events={{
                        keydown: (e) => {
                            clickHandler(e.key);
                        }
                    }}
                    enabled={true}
                />
            )}
            {isVR && (
                <a-entity position="0 -1 -1">
                    <BackgroundPanel
                        width={KEYBOARD_WIDTH}
                        height={KEYBOARD_HEIGHT}
                        color={backgroundColor}
                        side="double"
                        opacity="0.9"
                        scale="3 3 3"
                        rotation="-30 0 0"
                    >
                        {drawButton()}
                    </BackgroundPanel>
                </a-entity>
            )}
        </a-entity>
    );
}

Keyboard.propTypes = {
    setKeyboard: PropTypes.func.isRequired,
    state: PropTypes.object.isRequired,
    isVR: PropTypes.bool.isRequired
};

export default Keyboard;
